import React, { useEffect, useState, useCallback, useRef } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { fmtDate } from "../lib/helpers";
import * as Icons from "lucide-react";

export const StarRating = ({ value, onChange, readOnly, size = 16, testid }) => (
  <div className="flex items-center gap-1" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" disabled={readOnly}
        onClick={() => !readOnly && onChange(n)}
        data-testid={testid ? `${testid}-${n}` : undefined}
        className={`transition-transform ${readOnly ? "cursor-default" : "hover:scale-110"}`}>
        <Icons.Star size={size} strokeWidth={1.5}
          className={n <= (value || 0) ? "fill-ambre-vif text-ambre-vif" : "text-sable-fonce"} />
      </button>
    ))}
  </div>
);

// Modal global : demandes d'évaluation en attente, réapparaît tant que non complétées.
export const EvaluationModal = () => {
  const [pending, setPending] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ overall: 0, satisfaction: 0, scores: {}, comment: "" });
  const [saving, setSaving] = useState(false);
  const roleRef = useRef(null);

  const fetchPending = useCallback(async () => {
    try {
      const r = await api.get("/me/evaluations/pending");
      setPending(r.data || []);
      if ((r.data || []).length > 0) setOpen(true);
    } catch { /* not authenticated / no access */ }
  }, []);

  useEffect(() => {
    fetchPending();
    const t = setInterval(fetchPending, 30000);
    window.addEventListener("eval-refresh", fetchPending);
    return () => { clearInterval(t); window.removeEventListener("eval-refresh", fetchPending); };
  }, [fetchPending]);

  const current = pending[0];
  useEffect(() => {
    if (current && roleRef.current !== current.id) {
      roleRef.current = current.id;
      setForm({ overall: 0, satisfaction: 0, scores: {}, comment: "" });
    }
  }, [current]);

  if (!open || !current) return null;
  const isAgency = current.role === "agency";
  const setScore = (k, v) => setForm((f) => ({ ...f, scores: { ...f.scores, [k]: v } }));

  const submit = async () => {
    if (!form.satisfaction || current.criteria.some((c) => !form.scores[c.key]) || (isAgency && !form.overall)) {
      toast.error("Merci de renseigner toutes les notes."); return;
    }
    setSaving(true);
    try {
      await api.post(`/evaluations/${current.id}/submit`, {
        overall: isAgency ? form.overall : null,
        satisfaction: form.satisfaction,
        scores: form.scores,
        comment: form.comment,
      });
      toast.success("Évaluation envoyée. Merci !");
      roleRef.current = null;
      await fetchPending();
      if (pending.length <= 1) setOpen(false);
    } catch { toast.error("Erreur lors de l'envoi."); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-voile/60 backdrop-blur-sm p-4" data-testid="evaluation-modal">
      <div className="bg-sable rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between p-5 border-b border-marine/10 sticky top-0 bg-sable">
          <div>
            <div className="flex items-center gap-2 text-marine"><h2 className="font-display text-xl">Évaluation de fin de mission</h2></div>
            <p className="text-xs text-marine/80 mt-1">{current.mission_label}{current.project_name ? ` · ${current.project_name}` : ""}</p>
            {isAgency && <p className="text-[11px] text-bordeaux mt-1 flex items-center gap-1"><Icons.Lock size={16} /> Évaluation interne — non visible par le collaborateur</p>}
            {pending.length > 1 && <p className="text-[11px] text-marine/80 mt-1">{pending.length} évaluations en attente</p>}
          </div>
          <button onClick={() => setOpen(false)} data-testid="eval-close" title="Plus tard" className="text-marine hover:opacity-70"><Icons.X size={16} /></button>
        </div>

        <div className="p-5 space-y-5">
          {isAgency && (
            <div className="flex items-center justify-between" data-testid="eval-overall">
              <label className="text-sm font-medium text-marine">Note globale</label>
              <StarRating value={form.overall} onChange={(v) => setForm((f) => ({ ...f, overall: v }))} testid="eval-overall-stars" />
            </div>
          )}
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-marine">Satisfaction générale</label>
            <StarRating value={form.satisfaction} onChange={(v) => setForm((f) => ({ ...f, satisfaction: v }))} testid="eval-satisfaction-stars" />
          </div>
          <div className="border-t border-marine/10 pt-4 space-y-3">
            <p className="text-xs uppercase tracking-wider text-marine/80">Critères</p>
            {current.criteria.map((c) => (
              <div key={c.key} className="flex items-center justify-between">
                <label className="text-sm text-marine">{c.label}</label>
                <StarRating value={form.scores[c.key]} onChange={(v) => setScore(c.key, v)} size={16} testid={`eval-crit-${c.key}`} />
              </div>
            ))}
          </div>
          <div>
            <label className="text-sm font-medium text-marine block mb-1">Commentaire {isAgency ? "interne" : "libre"}</label>
            <textarea value={form.comment} onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))}
              rows={3} data-testid="eval-comment" placeholder="Votre retour sur cette collaboration…"
              className="w-full text-sm border border-marine/20 rounded-sm p-2.5 focus:outline-none focus:border-marine" />
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 p-5 border-t border-marine/10 sticky bottom-0 bg-sable">
          <button onClick={() => setOpen(false)} data-testid="eval-later" className="text-sm text-marine/80 hover:opacity-70">Plus tard</button>
          <button onClick={submit} disabled={saving} data-testid="eval-submit"
            className="bg-marine text-white rounded-sm px-5 py-2.5 text-sm hover:bg-marine-hover transition-colors disabled:opacity-50">{saving ? "Envoi…" : "Envoyer l'évaluation"}</button>
        </div>
      </div>
    </div>
  );
};

const EvalCard = ({ e }) => (
  <div className="border border-marine/10 rounded-sm p-4" data-testid={`eval-view-${e.role}`}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-sm font-medium text-marine">{e.role === "agency" ? "Évaluation de l'agence (interne)" : "Évaluation du collaborateur"}</span>
      <div className="flex items-center gap-3 text-xs">
        {e.overall != null && <span className="flex items-center gap-1 text-ambre-vif"><Icons.Star size={16} className="fill-ambre-vif" /> {e.overall}/5</span>}
        <span className="flex items-center gap-1 text-marine/80">Satisfaction {e.satisfaction ?? "—"}/5</span>
      </div>
    </div>
    <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
      {(e.criteria || []).map((c) => (
        <div key={c.key} className="flex items-center justify-between border-b border-marine/5 py-1">
          <span className="text-marine/80">{c.label}</span>
          <span className="flex items-center gap-0.5">{[1, 2, 3, 4, 5].map((n) => <Icons.Star key={n} size={16} className={n <= (e.scores?.[c.key] || 0) ? "fill-ambre-vif text-ambre-vif" : "text-sable-fonce"} />)}</span>
        </div>
      ))}
    </div>
    {e.comment && <p className="text-xs text-marine mt-2 italic">« {e.comment} »</p>}
  </div>
);

// Bloc évaluations sur la fiche mission (admin) + bouton de clôture.
export const MissionEvaluations = ({ mid }) => {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get(`/missions/${mid}/evaluations`).then((r) => setData(r.data)).catch(() => setData(null)), [mid]);
  useEffect(() => {
    load();
    window.addEventListener("eval-refresh", load);
    return () => window.removeEventListener("eval-refresh", load);
  }, [load]);

  const statuses = data?.statuses || {};
  const hasEvals = Object.keys(statuses).length > 0;
  const StatusPill = ({ label, st }) => (
    <span className={`inline-flex items-center gap-1 text-[9px] px-2 py-1 rounded-sm border ${st === "submitted" ? "bg-bleu-50 text-bleu border-bleu/30" : "bg-ambre-50 text-ambre-texte border-ambre/30"}`}>
      {label} : {st === "submitted" ? "envoyée" : "en attente"}
    </span>
  );

  return (
    <div className="bg-sable rounded-sm border border-marine/25 p-5 mt-8" data-testid="mission-evaluations">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-2"><h2 className="font-display text-xl text-marine">Évaluation de la mission</h2></div>
      </div>
      {!hasEvals && <p className="text-sm text-marine/80">Sélectionnez « Terminée » dans le statut de la mission (en haut de la fiche) à la fin de la prestation pour déclencher l'évaluation réciproque (agence + collaborateur).</p>}
      {hasEvals && (
        <>
          <div className="flex flex-wrap gap-2 mb-4">
            <StatusPill label="Agence" st={statuses.agency} />
            <StatusPill label="Collaborateur" st={statuses.collaborator} />
          </div>
          {!data.both_submitted && <p className="text-xs text-ambre-texte bg-ambre-50 border border-ambre/40 rounded-sm px-3 py-2 mb-4 flex items-center gap-2"><Icons.EyeOff size={16} /> Double insu : les évaluations ne seront consultables qu'une fois les deux formulaires envoyés.</p>}
          <div className="space-y-3">
            {(data.evaluations || []).filter((e) => !e.masked).map((e) => <EvalCard key={e.role} e={e} />)}
          </div>
        </>
      )}
    </div>
  );
};
